<template>
  <v-card flat>
    <v-card-title class="page-head">
      <div class="ph-left">
        <div class="ph-icon"><v-icon color="#4527a0">mdi-chart-box-outline</v-icon></div>
        <div>
          <div class="ph-title">Summary Report</div>
          <div class="ph-sub">Assessment summary by date range</div>
        </div>
      </div>
      <v-spacer />
      <v-btn outlined small color="#2e7d32" class="mr-2 export-btn" @click="exportReport">
        <v-icon left small>mdi-file-excel-outline</v-icon>
        Export
      </v-btn>
      <v-btn class="ph-btn" @click="printReport">
        <v-icon left small>mdi-printer</v-icon>
        Print
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- Filters: visible on screen, hidden in print -->
      <v-row dense class="no-print align-center">
        <v-col cols="12" sm="4" md="3">
          <v-text-field
            v-model="filters.from_date"
            type="date"
            label="From date"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-calendar-start"
          />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <v-text-field
            v-model="filters.to_date"
            type="date"
            label="To date"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-calendar-end"
          />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="filters.district"
            :items="districts"
            item-text="district_name"
            item-value="district_name"
            label="District"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-map-marker-outline"
          />
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center justify-md-end">
          <v-btn text small class="mr-2" @click="resetFilters">
            <v-icon left small>mdi-refresh</v-icon>Reset
          </v-btn>
          <v-btn class="ph-btn" small @click="runReport">
            <v-icon left small>mdi-filter</v-icon>Apply
          </v-btn>
        </v-col>
      </v-row>

      <!-- Everything inside this container will be printed: letterhead + KPIs + table -->
      <div id="summary-report-print">

        <div class="print-header">
          <img :src="logoSrc" alt="Logo" class="print-logo" />
          <div class="print-head-text">
            <div class="print-org">Ministry of Women, Family Affairs &amp; Human Rights</div>
            <div class="print-report-title">
              Assessment Summary Report
              <span v-if="filters.district"> — {{ filters.district }}</span>
              <span v-if="filters.from_date || filters.to_date">
                ({{ filters.from_date || '…' }} — {{ filters.to_date || '…' }})
              </span>
            </div>
          </div>
          <div class="print-date">Generated<br /><strong>{{ currentDate }}</strong></div>
        </div>

        <!-- KPIs -->
        <v-row class="summary-kpi-row" dense>
          <v-col cols="6" md="3" v-for="k in kpiTiles" :key="k.label">
            <div class="sum-tile">
              <div class="sum-tile-icon" :style="{ background: k.bg }">
                <v-icon :color="k.color">{{ k.icon }}</v-icon>
              </div>
              <div>
                <div class="sum-tile-value">{{ k.value }}</div>
                <div class="sum-tile-label">{{ k.label }}</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Nutrition breakdown table -->
        <div class="form-section mt-5">Nutrition Status Breakdown</div>
        <v-data-table
          class="elevation-0"
          :headers="headers"
          :items="nutritionRows"
          dense
          hide-default-footer
        >
          <template v-slot:item.nutrition_status="{ item }">
            <v-chip
              small
              :color="['Excellent','Good'].includes(item.nutrition_status) ? '#e8f5e9' : (item.nutrition_status === 'Fair' ? '#fff8e1' : '#ffebee')"
              :text-color="['Excellent','Good'].includes(item.nutrition_status) ? '#2e7d32' : (item.nutrition_status === 'Fair' ? '#f57f17' : '#c62828')"
            >
              {{ item.nutrition_status }}
            </v-chip>
          </template>
          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="44" color="#b0bec5">mdi-chart-box-outline</v-icon>
              <div class="empty-text">No data for the selected range</div>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from 'axios';
import LogoImg from '@/assets/realmow.png';
import { exportCsv } from '@/utils/reportExport';

const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: 'SummaryReport',
  data() {
    return {
      filters: {
        from_date: '',
        to_date: '',
        district: '',
      },
      districts: [],
      headers: [
        { text: 'Nutrition Status', value: 'nutrition_status' },
        { text: 'Visits', value: 'count' },
      ],
      summary: {
        totalVisits: 0,
        sickVisits: 0,
        childrenCount: 0,
        mothersCount: 0,
      },
      nutritionRows: [],
      logoSrc: LogoImg,
      currentDate: new Date().toLocaleDateString(),
    };
  },
  computed: {
    kpiTiles() {
      return [
        { label: 'Total Visits',      value: this.summary.totalVisits,   icon: 'mdi-clipboard-check-outline', color: '#1565c0', bg: '#e3f0fd' },
        { label: 'Sick Visits',       value: this.summary.sickVisits,    icon: 'mdi-hospital-box-outline',    color: '#c62828', bg: '#fdecea' },
        { label: 'Children Assessed', value: this.summary.childrenCount, icon: 'mdi-human-child',             color: '#00838f', bg: '#e0f7fa' },
        { label: 'Care Givers',       value: this.summary.mothersCount,  icon: 'mdi-account-heart-outline',   color: '#6a1b9a', bg: '#f3e5f5' },
      ];
    },
  },
  mounted() {
    this.runReport();
    this.fetchDistricts();
  },
  methods: {
    fetchDistricts() {
      axios.post(API_URL, { opcion: 40 }).then((res) => {
        this.districts = res.data || [];
      }).catch(() => { this.districts = []; });
    },
    resetFilters() {
      this.filters.from_date = '';
      this.filters.to_date = '';
      this.filters.district = '';
      this.runReport();
    },
    runReport() {
      axios
        .post(API_URL, { opcion: 20, op: 0, assessedby: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then((res) => {
          const all = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];

          const filtered = all.filter((r) => {
            if (this.filters.from_date && r.visit_date < this.filters.from_date) return false;
            if (this.filters.to_date && r.visit_date > this.filters.to_date) return false;
            if (this.filters.district && r.district !== this.filters.district) return false;
            return true;
          });

          this.summary.totalVisits = filtered.length;
          this.summary.sickVisits = filtered.filter((r) => String(r.is_sick) === '1').length;
          this.summary.childrenCount = new Set(filtered.map((r) => r.child_id)).size;
          this.summary.mothersCount = new Set(filtered.map((r) => r.mother_id)).size;

          const byNutrition = {};
          filtered.forEach((r) => {
            const key = r.nutrition_status || 'Unknown';
            byNutrition[key] = (byNutrition[key] || 0) + 1;
          });

          this.nutritionRows = Object.keys(byNutrition).map((k) => ({
            nutrition_status: k,
            count: byNutrition[k],
          }));
        })
        .catch(() => {
          this.summary = {
            totalVisits: 0,
            sickVisits: 0,
            childrenCount: 0,
            mothersCount: 0,
          };
          this.nutritionRows = [];
        });
    },
    exportReport() {
      const rows = [
        { metric: 'Total Visits', value: this.summary.totalVisits },
        { metric: 'Sick Visits', value: this.summary.sickVisits },
        { metric: 'Children Assessed', value: this.summary.childrenCount },
        { metric: 'Care Givers', value: this.summary.mothersCount },
        { metric: '', value: '' },
        { metric: 'Nutrition Status', value: 'Visits' },
        ...this.nutritionRows.map((r) => ({ metric: r.nutrition_status, value: r.count })),
      ];
      exportCsv('assessment-summary-report', [
        { text: 'Metric', value: 'metric' },
        { text: 'Value', value: 'value' },
      ], rows);
    },
    printReport() {
      const prtHtml = document.getElementById('summary-report-print')?.innerHTML;
      if (!prtHtml) {
        window.print();
        return;
      }

      let stylesHtml = '';
      const styleNodes = document.querySelectorAll('link[rel="stylesheet"], style');
      styleNodes.forEach((node) => {
        stylesHtml += node.outerHTML;
      });

      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');
      WinPrint.document.write(`<!DOCTYPE html>
        <html>
          <head>
            ${stylesHtml}
          </head>
          <body>
            ${prtHtml}
          </body>
        </html>`);
      WinPrint.document.close();

      WinPrint.onload = () => {
        WinPrint.focus();
        WinPrint.print();
        WinPrint.close();
      };
    },
  },
};
</script>

<style scoped>
@media print {
  /* Keep the four KPI tiles on a single row when printing */
  .summary-kpi-row > .v-col {
    max-width: 25% !important;
    flex: 0 0 25% !important;
  }
}
</style>
